import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAudio } from '../contexts/AudioContext';
import {
  Home, Briefcase, Calendar, MessageCircle, User, Menu, Bell,
  LogOut, Heart, ShoppingBag, Building, Users, Link as LinkIcon,
  ChevronDown, Shield, CalendarDays, Rss, BookOpen, Video,
  Star, X, Settings, GraduationCap, Megaphone, HelpCircle, Clapperboard,
  Key, Landmark, Music
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useState, useEffect } from 'react';
import { notificationsAPI } from '../lib/api';
import { useCommunity } from '../contexts/CommunityContext';
import usePageTracking from '../hooks/usePageTracking';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';

const Layout = ({ children }) => {
  usePageTracking();
  const { user, logout } = useAuth();
  const { currentTrack } = useAudio();
  const location = useLocation();
  const navigate = useNavigate();
  // Bell counts non-message unread notifications; the new Mail icon counts DMs separately.
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { communityView, setCommunityView, communityLabel, communityOptions, isSpelhouse } = useCommunity();

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await notificationsAPI.list({ unread_only: true });
        const list = Array.isArray(res.data) ? res.data : [];
        // Split: DMs get their own icon; everything else stays on the bell.
        const msgs = list.filter(n => n.type === 'message').length;
        setUnreadMessages(msgs);
        setUnreadCount(list.length - msgs);
      } catch (e) {
        // silent
      }
    };
    fetchUnread();
  }, [location.pathname]);

  // Crisp chat: set user data once on login, retry until Crisp is ready
  useEffect(() => {
    if (!user || window._crispUserSet) return;

    const initCrisp = () => {
      if (!window.$crisp || !document.querySelector('.crisp-client')) return false;
      try {
        window.$crisp.push(['set', 'user:email', [user.email]]);
        window.$crisp.push(['set', 'user:nickname', [user.name]]);
        if (user.institution) {
          window.$crisp.push(['set', 'session:data', [[
            ['institution', user.institution],
            ['user_id', user.user_id || ''],
            ['role', user.role || 'user'],
          ]]]);
        }
        const isMobile = window.innerWidth <= 767;
        if (isMobile) {
          window.$crisp.push(['do', 'chat:hide']);
        } else {
          window.$crisp.push(['do', 'chat:show']);
        }
        window._crispUserSet = true;
        return true;
      } catch (e) { return false; }
    };

    // Try immediately, then retry every 2s for up to 10s
    if (!initCrisp()) {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (initCrisp() || attempts >= 5) clearInterval(interval);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/feed', icon: Home, label: 'Feed' },
    { path: '/jobs', icon: Briefcase, label: 'Jobs' },
    { path: '/chat', icon: MessageCircle, label: 'Chat' },
    { path: '/marketplace', icon: ShoppingBag, label: 'Market' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  const moreItems = [
    { path: '/clips', icon: Clapperboard, label: 'Clips' },
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/hbcu-media', icon: Music, label: 'HBCU Media' },
    { path: '/events', icon: Calendar, label: 'Events' },
    { path: '/rentals', icon: Key, label: 'Rentals' },
    { path: '/real-estate', icon: Landmark, label: 'Real Estate' },
    { path: '/dating', icon: Heart, label: 'Dating' },
    { path: '/mentorship', icon: Users, label: 'Mentorship' },
    { path: '/business-services', icon: Briefcase, label: 'Business Services' },
    { path: '/blog', icon: BookOpen, label: 'Blog' },
    { path: '/notifications', icon: Bell, label: 'Notifications' },
    { path: '#support', icon: HelpCircle, label: 'Support', action: 'crisp' },
    ...(user?.role === 'admin' || user?.roles?.includes('admin') ? [{ path: '/admin', icon: Shield, label: 'Admin' }] : []),
    ...(user?.roles?.includes('alumni_association') || user?.roles?.includes('admin') ? [{ path: '/alumni-association', icon: GraduationCap, label: 'AA Panel' }] : []),
    ...(user?.roles?.includes('recruiter') || user?.roles?.includes('admin') ? [{ path: '/recruiter', icon: Briefcase, label: 'Recruiter' }] : []),
    ...(user?.roles?.includes('sponsor') || user?.roles?.includes('advertiser') || user?.roles?.includes('admin') ? [{ path: '/sponsor', icon: Megaphone, label: 'Sponsor' }] : []),
  ];

  return (
    <div className="min-h-screen bg-frost-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/50" style={{ backgroundColor: 'hsl(var(--card) / 0.85)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center space-x-2" data-testid="logo-link">
              <div className="w-8 h-8 bg-dark-slate rounded-lg flex items-center justify-center transform rotate-[15deg]">
                <LinkIcon className="w-5 h-5 text-mint-fresh" />
              </div>
              <span className="font-heading font-bold text-xl text-dark-slate hidden sm:block">GradLink</span>
            </Link>
            {/* Institution badge — mobile only (toggle replaces it on desktop) */}
            {user?.institution && (
              <span className="md:hidden inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 ml-1 truncate max-w-[120px]">
                {communityLabel}
              </span>
            )}

            {/* Community Toggle (center, desktop) */}
            {user?.institution && (
              <div className="hidden md:flex items-center rounded-full p-1" style={{ backgroundColor: 'hsl(var(--secondary))' }}>
                {communityOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setCommunityView(opt.value)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      communityView === opt.value
                        ? 'text-dark-slate shadow-sm'
                        : 'text-slate-gray hover:text-dark-slate'
                    }`}
                    style={communityView === opt.value ? { backgroundColor: 'hsl(var(--card))' } : {}}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-colors ${
                    location.pathname === item.path
                      ? 'bg-mint-fresh text-dark-slate'
                      : 'text-slate-gray hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}

              {/* More dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-1 rounded-full" data-testid="nav-more">
                    <Menu className="w-5 h-5" />
                    <span className="font-medium">More</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {moreItems.map(item => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link to={item.path} className="flex items-center space-x-2" data-testid={`nav-${item.label.toLowerCase()}`}>
                        <item.icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Right side */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Messages (DMs) */}
              <Link
                to="/chat"
                className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="messages-link"
                aria-label="Messages"
              >
                <MessageCircle className="w-5 h-5 text-slate-gray" />
                {unreadMessages > 0 && (
                  <span className="absolute top-0 right-0 min-w-[20px] h-5 px-1 bg-emerald-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </Link>

              {/* Notifications */}
              <Link
                to="/notifications"
                className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="notifications-link"
              >
                <Bell className="w-5 h-5 text-slate-gray" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>

              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2 rounded-full px-2 sm:px-3" data-testid="user-menu">
                    <Avatar className="w-8 h-8 pointer-events-none">
                      <AvatarImage src={user?.profile_photo} alt={user?.name} />
                      <AvatarFallback className="bg-mint-fresh text-dark-slate">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block font-medium text-dark-slate max-w-[120px] truncate">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-gray" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-dark-slate">{user?.name}</p>
                    <p className="text-sm text-slate-gray truncate">{user?.email}</p>
                    {user?.institution && (
                      <p className="text-sm text-emerald-600 font-medium">{user?.institution}</p>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center space-x-2">
                      <User className="w-4 h-4" /><span>Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center space-x-2">
                      <Home className="w-4 h-4" /><span>Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/business-services" className="flex items-center space-x-2">
                      <Briefcase className="w-4 h-4" /><span>Business Services</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center space-x-2">
                      <Settings className="w-4 h-4" /><span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /><span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main content — extra bottom padding on mobile when player is active */}
      <main className={`flex-1 overflow-y-auto ${currentTrack ? 'pb-36 md:pb-8' : 'pb-20 md:pb-8'}`}>
        {children}
      </main>

      {/* Mobile floating help button — shifts up when player is active */}
      <button
        onClick={() => {
          if (window.openCrispChat) window.openCrispChat();
        }}
        className="md:hidden fixed z-50 w-12 h-12 rounded-full bg-dark-slate text-white shadow-lg flex items-center justify-center active:scale-95 transition-all"
        style={{ bottom: currentTrack ? '148px' : '76px', right: '16px' }}
        aria-label="Help & Support"
      >
        <MessageCircle className="w-5 h-5" />
      </button>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 border-t z-50 safe-area-pb" style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}>
        <div className="flex justify-around py-2">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center py-1.5 px-3 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'text-dark-slate'
                  : 'text-slate-gray'
              }`}
            >
              <item.icon className={`w-6 h-6 ${location.pathname === item.path ? 'text-dark-slate' : ''}`} />
              <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
            </Link>
          ))}
          <button
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-nav-menu"
            className="flex flex-col items-center py-1.5 px-3 rounded-lg text-slate-gray"
          >
            <Menu className="w-6 h-6" />
            <span className="text-[10px] mt-0.5 font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* Mobile More Menu Sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[80vh]">
          <SheetHeader>
            <SheetTitle className="font-heading text-left">Menu</SheetTitle>
          </SheetHeader>

          {/* Community toggle (mobile) */}
          {user?.institution && (
            <div className="flex items-center rounded-full p-1 mt-4 mb-4" style={{ backgroundColor: 'hsl(var(--secondary))' }}>
              {communityOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setCommunityView(opt.value)}
                  className={`flex-1 px-3 py-2 rounded-full text-xs font-medium transition-colors text-center ${
                    communityView === opt.value
                      ? 'text-dark-slate shadow-sm'
                      : 'text-slate-gray'
                  }`}
                  style={communityView === opt.value ? { backgroundColor: 'hsl(var(--card))' } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 mt-2 pb-6">
            {moreItems.map(item => (
              item.action === 'crisp' ? (
                <button
                  key={item.path}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setTimeout(() => {
                      if (window.openCrispChat) window.openCrispChat();
                    }, 300);
                  }}
                  className="flex flex-col items-center p-4 rounded-2xl transition-colors bg-slate-50 text-slate-gray hover:bg-slate-100"
                >
                  <item.icon className="w-6 h-6 mb-1.5" />
                  <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
                </button>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex flex-col items-center p-4 rounded-2xl transition-colors ${
                    location.pathname === item.path
                      ? 'bg-mint-fresh/30 text-dark-slate'
                      : 'bg-slate-50 text-slate-gray hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="w-6 h-6 mb-1.5" />
                  <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
                  {item.label === 'Notifications' && unreadCount > 0 && (
                    <span className="mt-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full font-medium">
                      {unreadCount}
                    </span>
                  )}
                </Link>
              )
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Layout;
